import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../../hooks/redux';
import Profile from './profile/Profile';
import DSPs from './dsp/Dsp';
import PitchPack from './pitch-pack/PitchPack';
import Privacy from './privacy/Privacy';
import Support from './support/Support';
import Billing from './billing/Billing';
import Token from './token/Token';

interface TabsProps {
  initialTab?: string | null;
  searchParams?: URLSearchParams;
  setSearchParams?: (params: URLSearchParams, options?: any) => void;
}

const Tabs: React.FC<TabsProps> = ({ initialTab, searchParams, setSearchParams }) => {
  const { user } = useAppSelector((state) => state.auth);

  const availableTabs = user?.role === 'admin' 
    ? ['profile', 'api-tokens']
    : ['profile', 'dsps', 'pitch-pack', 'privacy', 'support', 'billing', 'api-tokens'];

  // Get initial tab from URL or default
  const getInitialActiveTab = (): string => {
    const urlTab = searchParams?.get('tab');
    if (urlTab && availableTabs.includes(urlTab)) {
      return urlTab;
    }
    
    if (initialTab && availableTabs.includes(initialTab)) {
      return initialTab;
    }
    
    return availableTabs[0] || 'profile';
  };

  const [activeTab, setActiveTab] = useState<string>(getInitialActiveTab());

  // Update active tab when URL changes
  useEffect(() => {
    const urlTab = searchParams?.get('tab');
    
    if (urlTab && availableTabs.includes(urlTab)) {
      setActiveTab(urlTab);
    } else if (initialTab && availableTabs.includes(initialTab)) {
      setActiveTab(initialTab);
    }
  }, [searchParams, initialTab, availableTabs]);

  const handleTabClick = (tabKey: string) => {
    setActiveTab(tabKey);
    
    if (setSearchParams) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', tabKey);
      setSearchParams(newSearchParams, { replace: true });
    }
  };

  const tabConfig = [
    { name: 'Profile', key: 'profile' },
    { name: 'DSPs', key: 'dsps' },
    { name: 'Pitch Pack', key: 'pitch-pack' },
    { name: 'Privacy', key: 'privacy' },
    { name: 'Support', key: 'support' },
    { name: 'Billing', key: 'billing' },
    { name: 'API Tokens', key: 'api-tokens' },
  ];

  const filteredTabConfig = tabConfig.filter(tab => availableTabs.includes(tab.key));

  // Render component based on active tab
  const renderActiveComponent = () => {
    switch (activeTab) {
      case 'profile':
        return <Profile />;
      case 'dsps':
        return <DSPs />;
      case 'pitch-pack':
        return <PitchPack />;
      case 'privacy':
        return <Privacy />;
      case 'support':
        return <Support />;
      case 'billing':
        return <Billing />;
      case 'api-tokens':
        return <Token />;
      default:
        return <Profile />;
    }
  };

  return (
    <div>
      {/* Tabs Navigation */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="flex space-x-4 overflow-x-auto sm:space-x-8 sm:px-0">
          {filteredTabConfig.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabClick(tab.key)}
              className={`py-2 sm:py-4 px-2 text-sm sm:text-base focus:outline-none whitespace-nowrap transition-colors ${
                activeTab === tab.key
                  ? 'text-blue-600 border-b-2 border-blue-600 font-semibold'
                  : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </div>
      </div>

      {/* Render the active tab's component */}
      <div className="px-4 mt-4 sm:mt-6 sm:px-0">
        {renderActiveComponent()}
      </div>
    </div>
  );
};

export default Tabs;